/**
 * 3D case preview: scale to full schema width (40/60 layout right column),
 * vertically centered; overflow cropped to cell. Default pdfme image uses
 * "contain" and leaves horizontal gaps for portrait renders.
 */
import type { PDFRenderProps, Plugin, Schema } from "@pdfme/common";
import { image as pdfmeImage } from "@pdfme/schemas";
import { convertForPdfLayoutProps } from "@pdfme/schemas/utils";

function cacheKey(schema: Schema, input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    const codePoint = input.codePointAt(i);
    if (codePoint === undefined) continue;
    h ^= codePoint;
    h = Math.imul(h, 0x01000193);
    if (codePoint > 0xffff) i++;
  }
  const hex = (h >>> 0).toString(16).padStart(8, "0");
  return `${schema.type}:${input.length}:${hex}`;
}

async function casePreviewImagePdf(arg: PDFRenderProps<Schema>): Promise<void> {
  const { value, schema, pdfDoc, page, _cache, pdfLib } = arg;
  const { clip, endPath, popGraphicsState, pushGraphicsState, rectangle } =
    pdfLib;
  if (!value) return;

  if (schema.rotate) {
    await pdfmeImage.pdf(arg);
    return;
  }

  const inputImageCacheKey = cacheKey(schema, value);
  const isCachedPdfImage = (
    image: unknown,
  ): image is Awaited<ReturnType<typeof pdfDoc.embedPng>> => {
    return (
      typeof image === "object" &&
      image !== null &&
      "width" in image &&
      "height" in image
    );
  };
  const cachedPdfImage = _cache.get(inputImageCacheKey);
  let pdfImage: Awaited<ReturnType<typeof pdfDoc.embedPng>> | undefined =
    isCachedPdfImage(cachedPdfImage) ? cachedPdfImage : undefined;
  if (!pdfImage) {
    const isPng = value.startsWith("data:image/png;");
    pdfImage = await (isPng ? pdfDoc.embedPng(value) : pdfDoc.embedJpg(value));
    _cache.set(inputImageCacheKey, pdfImage);
  }

  const imageWidth = pdfImage.width;
  const imageHeight = pdfImage.height;
  if (imageWidth <= 0 || imageHeight <= 0) return;

  const imageRatio = imageWidth / imageHeight;
  const boxSchema = { ...schema, position: { ...schema.position } };
  const boxWidth = boxSchema.width;
  const boxHeight = boxSchema.height;

  const drawSchema = { ...schema, position: { ...schema.position } };
  drawSchema.width = boxWidth;
  drawSchema.height = boxWidth / imageRatio;
  drawSchema.position.y += (boxHeight - drawSchema.height) / 2;

  const pageHeight = page.getHeight();
  const clipProps = convertForPdfLayoutProps({ schema: boxSchema, pageHeight });
  const drawProps = convertForPdfLayoutProps({
    schema: drawSchema,
    pageHeight,
  });

  const { x: cx, y: cy } = clipProps.position;
  const cw = clipProps.width;
  const ch = clipProps.height;

  page.pushOperators(
    pushGraphicsState(),
    rectangle(cx, cy, cw, ch),
    clip(),
    endPath(),
  );

  page.drawImage(pdfImage, {
    x: drawProps.position.x,
    y: drawProps.position.y,
    width: drawProps.width,
    height: drawProps.height,
    rotate: drawProps.rotate,
    opacity: drawProps.opacity,
  });

  page.pushOperators(popGraphicsState());
}

export const casePreviewImageSchema: Plugin = {
  pdf: casePreviewImagePdf,
  ui: pdfmeImage.ui,
  // pdfme checkPlugins matches template schema.type to propPanel.defaultSchema.type
  propPanel: {
    ...pdfmeImage.propPanel,
    defaultSchema: {
      ...pdfmeImage.propPanel.defaultSchema,
      type: "casePreviewImage",
    },
  },
  icon: pdfmeImage.icon,
};

export default casePreviewImageSchema;
